// RADAR OPERATIVO (v2.14.0) — HOME operacional de la monitorista interna residencial.
// Observa y organiza; NO atiende: el caso se atiende en Central (deep-link ?caso=).
// Un solo request agregado (GET /api/radar/residencial) para toda la población: el número de
// peticiones NO crece con el número de personas. La capa global de alertas (GlobalAlertLayer /
// AlertsProvider) y su polling certificado NO se tocan: siguen operando por encima de esta vista.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PersonaDrawer from "@/components/persona/PersonaDrawer";
import RadarHeader from "@/components/radar/RadarHeader";
import RadarCard from "@/components/radar/RadarCard";
import VistaControlesRes from "@/components/radar/res/VistaControlesRes";
import CasosActivosTira from "@/components/radar/res/CasosActivosTira";
import useRadarPrefs from "@/hooks/useRadarPrefs";
import { filtrarResidencial, ordenarResidencial } from "@/lib/radar/ordenRes";
import {
  DENSIDAD_KEY, densidadValida, densidadEfectiva, autoExpandida, visibles, variante, COLUMNAS,
} from "@/lib/radar/densidad";

const REFRESCO_MS = 5000;

// v2.14.1 (Demo Parity) — `snapshot` permite inyectar un read-model YA construido en memoria
// (fixture DEMO) en lugar de consultar el backend: la MISMA UI, con el dataset aislado del demo.
export default function RadarOperativo({ snapshot = null, demoFichas = null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const esDemo = !!snapshot;
  const [snapRemoto, setSnap] = useState(null);
  const snap = snapshot || snapRemoto;
  const [fallo, setFallo] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [selId, setSelId] = useState(null);
  const [q, setQ] = useState("");
  // Primera entrada: preferencia válida del usuario; si no, «Todos» (toda la población scoped).
  const [densidad, setDensidad] = useState(() => {
    const guardada = localStorage.getItem(DENSIDAD_KEY);
    const v = guardada === "todos" ? "todos" : densidadValida(guardada);
    return v || "todos";
  });
  // Reloj del SERVIDOR (no del navegador): ventana de 30 min y horas se calculan con el
  // timestamp autoritativo del snapshot + el tiempo transcurrido localmente.
  const recibidoRef = useRef(0);
  const [tick, setTick] = useState(0);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/radar/residencial");
      setSnap(data);
      recibidoRef.current = Date.now();
      setFallo(false);
    } catch { setFallo(true); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => {
    if (esDemo) { setCargando(false); return; }
    cargar();
    const t = setInterval(cargar, REFRESCO_MS);
    return () => clearInterval(t);
  }, [esDemo, cargar]);

  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const elegir = (d) => { setDensidad(d); localStorage.setItem(DENSIDAD_KEY, String(d)); };

  const personas = snap?.personas || [];
  const resumen = snap?.resumen || { personas: 0, pendientes: 0, en_atencion: 0, actividad_reciente: 0, coberturas_revisar: 0, atenciones_activas: 0 };
  const ventanaMin = snap?.ventana_actividad_min || 30;
  // R3 · «Mi vista» residencial: IDs de PERSONA autorizados, locales, versionados y acotados al
  // usuario. Nunca se guardan nombres, expedientes ni casos; el ámbito es la cartera, no un sitio.
  const idsAutorizados = useMemo(() => personas.map((p) => p.id), [personas]);
  const { prefs, set: setPrefs, alternarId, persistente } = useRadarPrefs(
    user?.id || user?.email || (esDemo ? "demo" : null), "cartera", idsAutorizados, "res");
  const [editandoVista, setEditandoVista] = useState(false);
  const favoritos = useMemo(() => new Set(prefs.miVista || []), [prefs.miVista]);
  const ordenRes = prefs.orden === "nombre" || prefs.orden === "sitio" ? prefs.orden : "prioridad";
  const nowMs = useMemo(() => {
    if (esDemo || !snap?.ahora) return Date.now();
    return new Date(snap.ahora).getTime() + (Date.now() - recibidoRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap, tick]);

  const filtradas = useMemo(() => {
    // R4 · búsqueda por nombre O por sitio/domicilio, con los campos que el read-model YA entrega.
    // R3 · el modo de cartera (todas | mi vista | con caso) es un filtro de PRESENTACIÓN.
    const base = filtrarResidencial(personas, { q, modo: prefs.modo, miVista: prefs.miVista });
    return ordenarResidencial(base, ordenRes);
  }, [personas, q, prefs.modo, prefs.miVista, ordenRes]);

  const activas = resumen.atenciones_activas || 0;
  const efectiva = densidadEfectiva(densidad, activas);
  const autoExp = autoExpandida(densidad, efectiva);
  const mostradas = q.trim() ? filtradas : visibles(filtradas, efectiva);
  const v = variante(efectiva, filtradas.length);
  // R5 · ninguna persona con caso abierto queda fuera de alcance por favoritos, búsqueda,
  // densidad u orden: las que no estén en la rejilla se ofrecen aparte.
  const visiblesIds = useMemo(() => new Set(mostradas.map((t) => t.id)), [mostradas]);
  const ocultasConCaso = useMemo(
    () => personas.filter((t) => t.caso && !visiblesIds.has(t.id)), [personas, visiblesIds]);
  const conCaso = useMemo(() => personas.filter((t) => t.caso).length, [personas]);

  const irACentral = (t) => {
    const aid = t.caso?.alarma_id;
    if (aid) navigate(`/consola-v3?caso=${encodeURIComponent(aid)}`);
  };

  return (
    <div data-testid="radar-operativo-page" className="gv2 tema-noche-auto flex h-full min-h-0 flex-col">
      <div className="sticky top-0 z-20 shrink-0">
        <RadarHeader resumen={resumen} live={!fallo} densidad={densidad} densidadEfect={efectiva}
          autoExp={autoExp} onDensidad={elegir} q={q} onQ={setQ} ventanaMin={ventanaMin}
          controles={snap ? (
            <VistaControlesRes prefs={{ ...prefs, orden: ordenRes }} onPref={setPrefs}
              editando={editandoVista} onEditar={() => setEditandoVista((x) => !x)}
              favoritos={favoritos.size} conCaso={conCaso} persistente={persistente} />
          ) : null} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-6 pt-3 sm:px-5 sm:pb-8 sm:pt-4">
        {cargando ? (
          <p className="p-10 text-center text-slate-400">Cargando población…</p>
        ) : fallo && personas.length === 0 ? (
          <p data-testid="radar-error" className="rounded-xl border border-dashed border-indigo-200 bg-indigo-50 p-8 text-center text-sm text-indigo-700">
            No se pudo consultar el padrón de personas. Reintentando; no hay estado que mostrar.
          </p>
        ) : personas.length === 0 ? (
          <p data-testid="radar-vacio" className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-400">
            No hay personas bajo monitoreo con tu nivel de acceso.
          </p>
        ) : mostradas.length === 0 ? (
          <>
            <CasosActivosTira ocultas={ocultasConCaso} onCentral={irACentral}
              onFicha={(t) => setSelId(t.id)} />
            <p data-testid="radar-sin-coincidencias" className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-slate-400">
              {q.trim()
                ? `Sin coincidencias para “${q}”.`
                : prefs.modo === "mi_vista"
                  ? "Tu vista no tiene personas elegidas todavía. Usa «Editar mi vista» y toca la estrella de una tarjeta."
                  : "No hay personas con caso activo ahora mismo."}
            </p>
          </>
        ) : (
          <>
            <CasosActivosTira ocultas={ocultasConCaso} onCentral={irACentral}
              onFicha={(t) => setSelId(t.id)} />
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <p data-testid="radar-conteo-visible" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {mostradas.length} de {filtradas.length} {filtradas.length === 1 ? "persona" : "personas"}
                {q.trim() ? " · filtrado por nombre o sitio" : ""}
                {prefs.modo === "mi_vista" ? " · mi vista" : prefs.modo === "con_caso" ? " · con caso activo" : ""}
              </p>
              <p className="text-[11px] text-slate-400">La actividad reciente es contexto: no crea casos.</p>
            </div>
            <div data-testid="radar-grid" className={`grid gap-3 sm:gap-4 ${COLUMNAS[v]}`}>
              {mostradas.map((t) => (
                <RadarCard key={t.id} t={t} variant={v} nowMs={nowMs} ventanaMin={ventanaMin}
                  favorito={favoritos.has(t.id)} editandoVista={editandoVista}
                  onFavorito={() => alternarId(t.id)}
                  onFicha={(x) => setSelId(x.id)} onCentral={irACentral} />
              ))}
            </div>
          </>
        )}
      </div>

      <PersonaDrawer open={!!selId} onClose={() => setSelId(null)}
        isDemo={!!demoFichas} demoFicha={demoFichas ? demoFichas[selId] || null : null}
        personaId={demoFichas ? null : selId} />
    </div>
  );
}
